import { useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";

import { Company } from "#modules/company/infra/Company.dto";
import { CompanySettings } from "#modules/company/infra/CompanySettings.dto";
import { DefaultProcessStepsSettings } from "#modules/company/view/DefaultProcessStepsSettings";
import { DefaultProductLinesSettings } from "#modules/company/view/DefaultProductLinesSettings";
import { MaterialsSettings } from "#modules/company/view/MaterialsSettings";
import { ProductsSettings } from "#modules/company/view/ProductsSettings";
import {
  CompanyTypeSection,
  DefaultMarginsSection,
  DefaultTransportSection,
  FinalizationSection,
  DisplaySection,
  ModelTypeSection,
  PartTypeMapperOverrideSection,
  SalesMarginSection,
  SearchResultSection,
} from "#modules/company/view/QuoteSettingsFormSections";
import { SurfaceTreatmentsSection } from "#modules/company/view/SurfaceTreatmentsSection";
import { useUpdateCompanySetting } from "#modules/company/view/useUpdateCompanySetting";
import { Icon } from "#shared/icons/Icon";
import { Flex } from "#shared/view/components/Flex";
import { Space } from "#shared/view/components/Space";
import { Stack } from "#shared/view/components/Stack";
import { Button } from "#shared/view/designSystem/Button";
import { PartTypeType } from "shared";

type Props = {
  company: Company;
};

export const QuoteSettingsForm = ({ company }: Props) => {
  const { updateCompanySetting, isPending: isCompanySettingLoading } = useUpdateCompanySetting();

  const handleOnSubmit: SubmitHandler<CompanySettings> = async (data) => {
    updateCompanySetting({
      ...data,
      companyType: data.companyType || null,
      partTypeMapperOverride: data.partTypeMapperOverride as Record<PartTypeType, PartTypeType>,
      defaultSheetFormat: data.defaultSheetFormat as { length: number; width: number } | null,
    });
  };

  const formMethods = useForm<CompanySettings>({
    defaultValues: company.companySettings,
  });
  const {
    handleSubmit,
    control,
    formState: { isDirty },
    reset,
    getValues,
  } = formMethods;

  useEffect(() => {
    if (!isDirty) {
      reset(company.companySettings);
    }
  }, [reset, company.companySettings, isDirty]);

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(handleOnSubmit)}>
        <Flex justify="flex-end">
          <Button
            variant="filled"
            icon={<Icon name="check" />}
            type="submit"
            mt="sm"
            loading={isCompanySettingLoading}
          >
            Enregistrer les modifications
          </Button>
        </Flex>

        <Stack gap="xs">
          <CompanyTypeSection control={control} getValues={getValues} />

          <PartTypeMapperOverrideSection />

          <ModelTypeSection control={control} />

          <SearchResultSection control={control} />

          <MaterialsSettings control={control} />

          <ProductsSettings />

          <DefaultProductLinesSettings control={control} />

          <DefaultProcessStepsSettings control={control} companyId={company.id} />

          <DefaultMarginsSection control={control} />

          <SalesMarginSection control={control} />

          <SurfaceTreatmentsSection />

          <DefaultTransportSection control={control} />

          <FinalizationSection control={control} />

          <DisplaySection control={control} />

          <Space h="lg" w="100%" />
        </Stack>

        <Flex justify="flex-end">
          <Button
            variant="filled"
            icon={<Icon name="check" />}
            type="submit"
            mt="sm"
            loading={isCompanySettingLoading}
          >
            Enregistrer les modifications
          </Button>
        </Flex>
      </form>
    </FormProvider>
  );
};
